import {
  selectCurrentUserId,
  selectCurrentUserIsVoltEmployee,
  selectCurrentUserPermissions,
} from "modules/auth/store/auth.slice";
import { uuidInStringPattern } from "utils/patterns";

import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";

import type { RootState } from "store";

export const loggingBaseQuery =
  <Args extends FetchArgs | string, Result, DefinitionExtraOptions, Meta>(
    baseQuery: BaseQueryFn<
      Args,
      Result,
      FetchBaseQueryError,
      DefinitionExtraOptions,
      Meta
    >
  ): BaseQueryFn<
    Args,
    Result,
    FetchBaseQueryError,
    DefinitionExtraOptions,
    Meta
  > =>
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    if (result.error) {
      const status = result.error.status;
      const url = typeof args === "string" ? args : args.url;

      if (
        typeof status === "number" &&
        status >= 400 &&
        status < 600 &&
        status !== 422 &&
        (status !== 404 || !uuidInStringPattern.test(url))
      ) {
        const method = typeof args === "string" ? "GET" : args.method || "GET";

        const state = api.getState() as RootState;
        const userId = selectCurrentUserId(state);
        const voltEmployee = selectCurrentUserIsVoltEmployee(state);
        const permissions = selectCurrentUserPermissions(state);
        const { location } = state.router;

        const service = (extraOptions as any)?.service;
        const env = (extraOptions as any)?.env;

        window.newrelic?.noticeError(
          new Error(`RTK Query Error: ${method} ${url} - ${status}`),
          {
            "request.statusCode": status,
            "request.url": url,
            "request.service": service,
            "request.env": env,
            "request.method": method,
            "request.responseData": JSON.stringify(result.error.data),
            "user.id": userId || "N/A",
            "user.voltEmployee": String(voltEmployee),
            "user.permissions": JSON.stringify(permissions),
            "http.pathname": location.pathname,
          }
        );
      }
    }

    return result;
  };
